import React, { useEffect, useMemo, useRef } from 'react';
import { useContext } from 'react';
import { StoreContext } from '../../store';
import { observer } from 'mobx-react-lite';
import {
  Box, Button, Divider, Grid, Link, List, ListItem, ListItemAvatar, ListItemText,
  Paper, Typography, Avatar, makeStyles, useMediaQuery, useTheme, IconButton
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import GetAppIcon from '@material-ui/icons/GetApp';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { Cog } from 'lucide-react';
import { RotateCwIcon } from 'lucide-react';
import { QrCodeIcon } from 'lucide-react';
import useTranslation from 'next-translate/useTranslation';

const useStyles = makeStyles((theme) => ({
  root: { background: '#F3EFE7', padding: theme.spacing(2), [theme.breakpoints.up('md')]: { padding: theme.spacing(4) } },
  banner: { display: 'flex', alignItems: 'center', padding: theme.spacing(2.5), borderRadius: 12 },
  bannerIconWrap: { width: 56, height: 56, borderRadius: 14, background: '#EAF6ED', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: theme.spacing(2) },
  card: { marginTop: theme.spacing(3), padding: theme.spacing(3), borderRadius: 16 },
  stepNum: { background: '#E8F5E9', color: '#1B5E20', fontWeight: 700 },
  qrWrap: { borderRadius: 16, border: '1px solid #e0e0e0', padding: theme.spacing(2), display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' },
  qrImg: { width: '100%', height: 'auto', maxWidth: 320, borderRadius: 8, display: 'block' },
  subtle: { color: theme.palette.text.secondary },
}));

export default observer(function WhatsAppScan({
  propertyId,
  phoneNumber,
  status,            // 'CONNECTED' | 'DISCONNECTED' | 'QR_REQUIRED' | 'ERROR' | undefined
  qrCode,            // data URL or null
  onSuccess,         // () => void
}) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const store = useContext(StoreContext);
  
  // Get real-time session data from the store
  const session = store.whatsapp.getSession(propertyId);
  const currentStatus = status || session?.status;
  const currentQrCode = qrCode || session?.qrCode;
  
  const isConnected = useMemo(() => String(currentStatus || '') === 'connected', [currentStatus]);

  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
  }, [propertyId]); 
  
  const successDoneRef = useRef(false);
  useEffect(() => {
    if (!onSuccess) return;
    if (isConnected && !successDoneRef.current) {
      successDoneRef.current = true;
      onSuccess();
    }
  }, [isConnected, onSuccess]);
  
  return (
    <Box className={classes.root}>
      {/* Banner */}
      <Paper elevation={0} className={classes.banner}>
        <Box className={classes.bannerIconWrap}><QrCodeIcon /></Box>
        <Box flex={1}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            {t('Scan the QR code below')}
          </Typography>
          <Typography variant="body2" color="textSecondary">• {t('Ask tenants to save this number as a contact.')}</Typography>
          <Typography variant="body2" color="textSecondary">• {t('Use the phone at least once every 14 days to keep it connected.')}</Typography>
        </Box>
      </Paper>

      {/* Card */}
      <Paper elevation={0} className={classes.card}>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={7}>
            <Typography variant={isXs ? 'h6' : 'h5'} gutterBottom style={{ fontWeight: 700 }}>
              {t('Steps to log in')}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemAvatar><Avatar className={classes.stepNum}>1</Avatar></ListItemAvatar>
                <ListItemText primary={<Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography style={{ fontWeight: 600, marginRight: 6 }}>{t('Open WhatsApp')}</Typography>
                  <WhatsAppIcon fontSize="small" className={classes.subtle} />
                  <Typography className={classes.subtle} style={{ marginLeft: 6 }}>{t('on your phone')}</Typography>
                </Box>} />
              </ListItem>
              <ListItem>
                <ListItemAvatar><Avatar className={classes.stepNum}>2</Avatar></ListItemAvatar>
                <ListItemText primary={<Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography className={classes.subtle} style={{ marginRight: 8 }}>{t('On Android tap Menu')}</Typography>
                  <MoreVertIcon fontSize="small" className={classes.subtle} />
                  <Typography className={classes.subtle} style={{ margin: '0 8px' }}>· {t('On iPhone tap Settings')}</Typography>
                  <Cog size={16} className={classes.subtle} />
                </Box>} />
              </ListItem>
              <ListItem>
                <ListItemAvatar><Avatar className={classes.stepNum}>3</Avatar></ListItemAvatar>
                <ListItemText primary={<Typography className={classes.subtle}>
                  {t('Tap')} <b>{t('Linked devices')}</b>, {t('then')} <b>{t('Link device')}</b>
                </Typography>} />
              </ListItem>
              <ListItem>
                <ListItemAvatar><Avatar className={classes.stepNum}>4</Avatar></ListItemAvatar>
                <ListItemText primary={<Typography className={classes.subtle}>
                  {t('Scan the QR code to confirm')}
                </Typography>} />
              </ListItem>
            </List>
          </Grid>

          {/* QR */}
          <Grid item xs={12} md={5}>
            <Box className={classes.qrWrap}>
              {currentQrCode ? (
                <img src={currentQrCode} alt="QR code" className={classes.qrImg} />
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <Typography variant="body2" className={classes.subtle}>
                    {isConnected ? t('Connected') : status === 'connecting' ? t('Connecting...') : t('Waiting for QR...')}
                  </Typography>

                  {!isConnected && status !== 'connecting' && (
                    <>
                      <Typography variant="body2" className={classes.subtle}>
                        {t('Refresh')}
                      </Typography>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          try {
                            store.whatsapp.startSession(propertyId);
                          } catch (err) {
                            // console.error('Failed to restart session:', err);
                          }
                        }}
                      >
                        <RotateCwIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Divider />
          <Box display="flex" justifyContent="center" mt={2}>
            <Typography variant="body2" className={classes.subtle}>
              {t('Read the rules and warnings')}
              {/* klik&nbsp;
              <Link href="#" color="primary" underline="always">Disini</Link> */}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
});
